import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import { useOrganization, useOrganizations } from '@/hooks/useOrganizations';
import { cn } from '@/lib/utils';
import { CheckCircleSolid, ChevronUpDown, Plus } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateOrganizationForm } from './onboarding/CreateOrganizationForm';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface iOrganizationsProps {}

const Organizations: FunctionComponent<iOrganizationsProps> = () => {
  const { organizations, organizationChange, refreshOrganizations } = useOrganizations();
  const organization = useOrganization();
  const isCollapsed = useAtomValue(sidebarCollapsedAtom);
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const _handleOpen = () => {
    setIsOpen(true);
  };

  const _handleClose = () => {
    setIsOpen(false);
  };

  const _handleCreateSuccess = async (_organizationId: string, slug?: string) => {
    setIsCreateDialogOpen(false);
    await refreshOrganizations();
    if (slug) {
      organizationChange(slug);
    }
  };

  if (!organization) return null;
  return (
    <div className={cn('h-15 p-2 group', isCollapsed && 'p-0 flex items-center justify-center')}>
      <div
        onClick={_handleOpen}
        className={cn(
          'w-full h-full select-none cursor-pointer flex items-center gap-2 pl-2 rounded-md hover:bg-slate-200/40 active:bg-slate-200/60 dark:hover:bg-stone-800/40 dark:active:bg-stone-800/60',
          isCollapsed && 'pl-0 size-10 items-center justify-center',
        )}>
        <div
          className={cn(
            'size-8 rounded overflow-hidden flex items-center justify-center',
            isCollapsed && 'size-8',
            !organization.logo?.url && 'bg-slate-200/40 dark:bg-stone-800/40',
          )}>
          {organization.logo?.url ? (
            <img src={organization.logo?.url} alt={organization.name} className='size-full object-cover' />
          ) : (
            <span className='text-sm font-semibold text-slate-500 dark:text-stone-100'>
              {organization.name.charAt(0)}
            </span>
          )}
        </div>
        {!isCollapsed && (
          <div className='flex-1 flex items-center justify-between pr-2'>
            <div className='flex flex-col gap-0.5'>
              <span className='text-sm font-medium leading-none line-clamp-1 overflow-ellipsis text-slate-700 dark:text-stone-100'>
                {organization.name}
              </span>
              <span className='text-xs leading-none font-medium line-clamp-1 overflow-ellipsis text-slate-500 dark:text-stone-100/70'>
                {t('organizations.enterprise')}
              </span>
            </div>
            <ChevronUpDown className='size-4 stroke-2 text-slate-500 dark:text-stone-200' />
          </div>
        )}
      </div>
      {isOpen && <div className='fixed z-20 inset-0' onClick={_handleClose} />}
      <div
        className={cn(
          'absolute z-50 select-none top-14 overflow-hidden border border-slate-200/70 left w-60 bg-white rounded-lg shadow-md/4 invisible opacity-0 transition-all -translate-y-2 dark:bg-stone-950 dark:border-stone-800    ',
          isCollapsed && 'fixed w-60 left-2 right-[unset] top-13 z-50',
          isOpen && 'visible opacity-100 translate-y-0 group-hover:translate-y-0',
        )}>
        <div className='p-1 bg-slate-50 border-b border-slate-100/70 dark:bg-stone-900 dark:border-stone-800 pl-2.5 py-1.5'>
          <span className='text-xs font-[550] line-clamp-1 overflow-ellipsis text-slate-700/70 dark:text-stone-100/70'>
            {t('organizations.title')}
          </span>
        </div>
        <div className='p-1'>
          {organizations.map((org) => (
            <div
              key={org.id}
              className={cn(
                'w-full p-1 flex rounded-md items-center gap-2 hover:bg-slate-100/50 active:bg-slate-100 dark:hover:bg-stone-800/40 dark:active:bg-stone-800/50 cursor-pointer',
              )}
              onClick={() => {
                _handleClose();
                organizationChange(org.slug);
              }}>
              <div
                className={cn(
                  'size-8 rounded overflow-hidden flex items-center justify-center',
                  !org.logo?.url && 'bg-slate-200/40 dark:bg-stone-800/40',
                )}>
                {org.logo?.url ? (
                  <img src={org.logo?.url} alt={org.name} className='size-full object-cover' />
                ) : (
                  <span className='text-sm font-semibold text-slate-500 dark:text-stone-100'>{org.name.charAt(0)}</span>
                )}
              </div>
              <span className='text-sm font-medium line-clamp-1 overflow-ellipsis text-slate-700 dark:text-stone-100 flex-1'>
                {org.name}
              </span>
              {organization.id === org.id && (
                <CheckCircleSolid className='size-4 stroke-2 text-slate-700 dark:text-stone-100 mr-1' />
              )}
            </div>
          ))}
        </div>
        <div className='p-1 border-t border-slate-100/70 dark:border-stone-800'>
          <div
            className='w-full p-1 flex rounded-md items-center gap-2 hover:bg-slate-100/50 active:bg-slate-100 dark:hover:bg-stone-800/40 dark:active:bg-stone-800/50 cursor-pointer'
            onClick={() => {
              _handleClose();
              setIsCreateDialogOpen(true);
            }}>
            <div className='size-8 rounded flex items-center justify-center bg-slate-200/40 dark:bg-stone-800/40'>
              <Plus className='size-4 stroke-2 text-slate-500 dark:text-stone-100' />
            </div>
            <span className='text-sm font-medium text-slate-700 dark:text-stone-100'>{t('organizations.createOrganization')}</span>
          </div>
        </div>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className='sm:max-w-md p-0'>
          <DialogHeader className='p-6 pb-0'>
            <DialogTitle>{t('organizations.createOrganization')}</DialogTitle>
            <DialogDescription>{t('organizations.createDescription')}</DialogDescription>
          </DialogHeader>
          <CreateOrganizationForm compact onSuccess={_handleCreateSuccess} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
